import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function PosPage() {
  const VAPID_PUBLIC_KEY =
    process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

  const [pushStatus, setPushStatus] = useState({
    loading: true,
    supported: true,
    permission: 'default',
    subscribed: false,
    message: '',
  });

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
  
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
  
    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i);
    }
  
    return outputArray;
  };

  const checkPushStatus = useCallback(async () => {
    if (
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      !('Notification' in window)
    ) {
      setPushStatus({
        loading: false,
        supported: false,
        permission: 'unsupported',
        subscribed: false,
        message: '이 브라우저는 Push 알림을 지원하지 않습니다.',
      });
      return;
    }
  
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription =
        await registration.pushManager.getSubscription();
  
      setPushStatus({
        loading: false,
        supported: true,
        permission: Notification.permission,
        subscribed: Boolean(subscription),
        message: subscription
          ? '주문 알림이 켜져 있습니다.'
          : '주문 알림이 꺼져 있습니다.',
      });
    } catch (error) {
      console.error('Push 상태 확인 실패:', error);
  
      setPushStatus({
        loading: false,
        supported: true,
        permission: Notification.permission,
        subscribed: false,
        message: 'Push 상태를 확인하지 못했습니다.',
      });
    }
  }, []);

  const [searchParams] = useSearchParams();

  const [boot, setBoot] = useState({
    loading: true,
    error: '',
    shop_id: 0,
    shop_name: '',
    canvas_w: 1000,
    canvas_h: 600,
    bg: '',
    tables: [],
  });

  const [scale, setScale] = useState(1);
  const [panelTitle, setPanelTitle] = useState('패널');
  const [panelHtml, setPanelHtml] = useState('<div class="p-3 text-muted">테이블을 선택하거나 <b>수동추가</b>를 누르세요.</div>');
  const [panelHint, setPanelHint] = useState('상태/금액이 여기에도 표시됩니다.');
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedTableName, setSelectedTableName] = useState('');
  const [panelMode, setPanelMode] = useState('');
  const [currentOrderId, setCurrentOrderId] = useState(0);
  const [currentOrderDate, setCurrentOrderDate] = useState('');
  const [stateMap, setStateMap] = useState({});
  const [liveStatus, setLiveStatus] = useState('ALL');
  const [livePage, setLivePage] = useState(1);

  const [liveOrders, setLiveOrders] = useState([]);
  const [liveMeta, setLiveMeta] = useState({
    latest_id: 0,
    new_count: 0,
    page: 1,
    total_pages: 1,
    total_count: 0,
    error: '',
    loading: true,
  });

  const [menuData, setMenuData] = useState({
    loading: false,
    error: '',
    shop_id: 0,
    table_id: 0,
    shop: null,
    table: null,
    token: '',
    category_id: 0,
    cats: [],
    products: [],
    cart_count: 0,
    added: 0,
    added_name: '',
  });

  const [cartData, setCartData] = useState({
    loading: false,
    error: '',
    shop_id: 0,
    table_id: 0,
    items: [],
    total: 0,
    can_place: false,
  });

  const [liveFirstLoaded, setLiveFirstLoaded] = useState(false);
  const [liveLastLatestId, setLiveLastLatestId] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [waitingSummary, setWaitingSummary] = useState({
    waiting_cnt: 0,
    called_cnt: 0,
    next_text: '다음: -',
  });
  const [kioskToast, setKioskToast] = useState({
    visible: false,
    code: '-',
    amount: '-',
    itemsHtml: '',
    token: '',
  });

  const [optionModal, setOptionModal] = useState({
    open: false,
    product: null,
    selected: {},
  });

  const floorWrapRef = useRef(null);
  const orderSoundRef = useRef(null);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const retryTimerRef = useRef(null);
  const pendingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  const getCurrentTableState = useCallback((tableId = selectedTableId) => {
    if (!tableId) return {};
    return stateMap[String(tableId)] || {};
  }, [stateMap, selectedTableId]);

  const escapeHtml = (s) => String(s ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const getLocalYmd = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const normalizeImageSrc = (path) => {
    const raw = String(path || '').trim();
    if (!raw) return '';
    if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
    if (raw.startsWith('/static/')) return raw;
    return `/static/${raw.replace(/^\/+/, '')}`;
  };

  const getItemOptions = (item) => {
    if (Array.isArray(item?.options)) return item.options;
    if (Array.isArray(item?.option_items)) return item.option_items;
    return [];
  };

  const getOptionName = (option) => {
    const groupName = option?.option_group_name || option?.group_name || '';
    const itemName = option?.option_item_name || option?.item_name || '';
    return groupName ? `${groupName}: ${itemName}` : itemName;
  };

  const getOptionPrice = (option) =>
    Number(option?.option_price ?? option?.extra_price ?? 0);

  const getDisplayPrice = (item) =>
    Number(
      item?.display_price ??
      item?.sale_price ??
      item?.product_price ??
      item?.price ??
      0
    );

  const getOriginalPrice = (item) =>
    Number(item?.original_price ?? item?.normal_price ?? item?.price ?? 0);

  const getUnitPrice = (item) =>
    Number(item?.unit_price ?? item?.price ?? 0);

  const buildOptionsHtml = (item) => {
    const options = getItemOptions(item);
    if (!options.length) return '';

    return `
      <div class="mt-1">
        ${options.map((opt) => {
          const name = getOptionName(opt);
          const extra = getOptionPrice(opt);
          return `
            <div class="small text-muted">
              · ${escapeHtml(name || '옵션')}
              ${extra !== 0 ? ` (${extra > 0 ? '+' : ''}${extra.toLocaleString()}원)` : ''}
            </div>
          `;
        }).join('')}
      </div>
    `;
  };

  const applyScale = useCallback(() => {
    if (!floorWrapRef.current) return;
    if (window.innerWidth <= 576) {
      setScale(0.68);
      return;
    }

    if (window.innerWidth <= 992) {
      setScale(0.80);
      return;
    }
    const floorWrap = floorWrapRef.current;
    const floorStyle = getComputedStyle(floorWrap);
    const padX = parseFloat(floorStyle.paddingLeft || 0) + parseFloat(floorStyle.paddingRight || 0);
    const padY = parseFloat(floorStyle.paddingTop || 0) + parseFloat(floorStyle.paddingBottom || 0);
    const availW = Math.max(0, floorWrap.clientWidth - padX);
    const availH = Math.max(0, floorWrap.clientHeight - padY);
    const fitX = availW / Math.max(boot.canvas_w || 1, 1);
    const fitY = availH / Math.max(boot.canvas_h || 1, 1);
    let nextScale = Math.min(fitX, fitY);
    if (!Number.isFinite(nextScale) || nextScale <= 0) nextScale = 1;
    if (nextScale > 1) nextScale = 1;
    setScale(nextScale);
  }, [boot.canvas_w, boot.canvas_h]);

  const playOrderVoice = useCallback(() => {
    if (!soundEnabled || !orderSoundRef.current) return;
    try {
      orderSoundRef.current.pause();
      orderSoundRef.current.currentTime = 0;
      orderSoundRef.current.play();
    } catch (_) {}
  }, [soundEnabled]);

  const enableSound = useCallback(async () => {
    setSoundEnabled(true);
    try {
      const audio = orderSoundRef.current;
      if (!audio) return;
      const prevVol = audio.volume;
      audio.volume = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.volume = prevVol;
    } catch (_) {}
  }, []);

  const enablePushNotifications = useCallback(async () => {
    if (pushStatus.loading) return;

    if (
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      !('Notification' in window)
    ) {
      alert('이 브라우저는 Push 알림을 지원하지 않습니다.');
      return;
    }

    if (!VAPID_PUBLIC_KEY) {
      alert(
        'VAPID 공개키가 설정되지 않았습니다.\n' +
        'React 빌드 환경변수 REACT_APP_VAPID_PUBLIC_KEY를 설정해주세요.'
      );
      return;
    }

    try {
      setPushStatus((prev) => ({
        ...prev,
        loading: true,
        message: '알림을 설정하는 중입니다.',
      }));

      let permission = Notification.permission;

      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }

      if (permission === 'denied') {
        setPushStatus({
          loading: false,
          supported: true,
          permission: 'denied',
          subscribed: false,
          message: '브라우저 설정에서 알림 권한을 허용해주세요.',
        });

        alert(
          '알림 권한이 차단되어 있습니다.\n' +
          '브라우저 사이트 설정에서 알림을 허용해주세요.'
        );
        return;
      }

      if (permission !== 'granted') {
        setPushStatus({
          loading: false,
          supported: true,
          permission,
          subscribed: false,
          message: '알림 권한이 허용되지 않았습니다.',
        });
        return;
      }

      const registration = await navigator.serviceWorker.ready;

      let subscription =
        await registration.pushManager.getSubscription();

      if (!subscription) {
        subscription =
          await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey:
              urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
          });
      }

      console.log(
        'POS Push 구독정보:',
        JSON.stringify(subscription.toJSON(), null, 2)
      );

      const saveResponse = await fetch('/api/pos/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          device_name: navigator.userAgent,
        }),
      });
      
      const saveData = await saveResponse.json();
      
      if (!saveResponse.ok || !saveData.success) {
        throw new Error(
          saveData.message || 'Push 구독정보 저장에 실패했습니다.'
        );
      }

      setPushStatus({
        loading: false,
        supported: true,
        permission: 'granted',
        subscribed: true,
        message: '주문 알림이 켜졌습니다.',
      });

      alert(
        '주문 알림 구독이 생성되었습니다.\n' +
        '현재는 콘솔에서 구독정보를 확인할 수 있습니다.'
      );
    } catch (error) {
      console.error('Push 구독 실패:', error);

      setPushStatus({
        loading: false,
        supported: true,
        permission: Notification.permission,
        subscribed: false,
        message: error?.message || 'Push 구독에 실패했습니다.',
      });

      alert(
        '주문 알림 설정에 실패했습니다.\n' +
        (error?.message || '')
      );
    }
  }, [
    VAPID_PUBLIC_KEY,
    pushStatus.loading,
  ]);

  const loadBootstrap = useCallback(async () => {
    try {
      const res = await fetch('/api/pos/api/bootstrap', {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const data = await res.json();

      if (!data.success) {
        setBoot((prev) => ({
          ...prev,
          loading: false,
          error: data.message || '초기화 실패',
        }));
        return;
      }

      setBoot({
        loading: false,
        error: '',
        shop_id: Number(data.shop_id || 0),
        shop_name: data.shop_name || '',
        canvas_w: Number(data.canvas_w || 1000),
        canvas_h: Number(data.canvas_h || 600),
        bg: data.bg || '',
        tables: Array.isArray(data.tables) ? data.tables : [],
      });
    } catch (e) {
      setBoot((prev) => ({
        ...prev,
        loading: false,
        error: e.message || '초기화 실패',
      }));
    }
  }, []);

  const updatePanelFooter = useCallback((tableId = selectedTableId, tableName = selectedTableName) => {
    if (!tableId) {
      setPanelHint('상태/금액이 여기에도 표시됩니다.');
      return;
    }
    const st = stateMap[String(tableId)] || {};
    const money = Number(st.money || 0);
    const status = st.status || 'IDLE';
    setPanelHint(
      `${tableName ? ` ${tableName} ` : ''} · 상태: ${status}` +
      (money > 0 ? ` · 금액: ${money.toLocaleString()}원` : '')
    );
  }, [selectedTableId, selectedTableName, stateMap]);

  const fetchStates = useCallback(async () => {
    try {
      const res = await fetch('/api/pos/table-state-api', {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const data = await res.json();
      if (!data.success) return;

      const nextMap = {};
      for (const t of data.tables || []) {
        nextMap[String(Number(t.table_id))] = {
          status: String(t.status || ''),
          money: Number(t.money || 0),
          has_addon: !!t.has_addon,
        };
      }
      setStateMap(nextMap);
    } catch (_) {}
  }, []);

  const fetchLiveOrders = useCallback(async (
    forceSound = false,
    statusArg = liveStatus,
    pageArg = livePage,
    options = {}
  ) => {
    const silent = !!options.silent;

    try {
      if (!silent) {
        setLiveMeta((prev) => ({
          ...prev,
          loading: true,
          error: '',
        }));
      } else {
        setLiveMeta((prev) => ({
          ...prev,
          error: '',
        }));
      }

      const res = await fetch(
        `/api/pos/admin-kiosk-orders-list?status=${encodeURIComponent(statusArg)}&page=${encodeURIComponent(pageArg)}`,
        {
          cache: 'no-store',
          credentials: 'same-origin',
        }
      );

      const data = await res.json();

      if (!res.ok || !data.success) {
        setLiveOrders([]);
        setLiveMeta((prev) => ({
          ...prev,
          loading: false,
          error: data.message || `주문목록 로드 실패 (${res.status})`,
          page: Number(pageArg || 1),
        }));
        return;
      }

      const rows = Array.isArray(data.rows) ? data.rows : [];
      const latestId = Number(data.latest_id || 0);

      setLiveOrders(rows);
      setLiveMeta({
        latest_id: latestId,
        new_count: Number(data.new_count || 0),
        page: Number(data.page || 1),
        total_pages: Number(data.total_pages || 1),
        total_count: Number(data.total_count || 0),
        error: '',
        loading: false,
      });

      if (!liveFirstLoaded) {
        setLiveFirstLoaded(true);
        setLiveLastLatestId(latestId);
        return;
      }

      if (forceSound || latestId > liveLastLatestId) {
        if (latestId > liveLastLatestId || forceSound) {
          playOrderVoice();
        }
      }

      if (latestId > liveLastLatestId) {
        setLiveLastLatestId(latestId);
      }
    } catch (e) {
      setLiveOrders([]);
      setLiveMeta((prev) => ({
        ...prev,
        loading: false,
        error: e.message || '주문목록 로드 실패',
      }));
    }
  }, [liveFirstLoaded, liveLastLatestId, livePage, liveStatus, playOrderVoice]);

  const loadWaitingSummary = useCallback(async () => {
    try {
      const fd = new FormData();
      fd.append('mode', 'summary');
      fd.append('shop_id', String(boot.shop_id));
  
      const res = await fetch('/api/seller/waiting/manage-api', {
        method: 'POST',
        body: fd,
        cache: 'no-store',
        credentials: 'same-origin',
      });
  
      const j = await res.json();
      if (!j.success) return;
  
      setWaitingSummary({
        waiting_cnt: j.waiting_cnt,
        called_cnt: j.called_cnt,
        next_text: j.next
          ? `다음 호출: #${j.next.waiting_number} ${j.next.name} (${j.next.people}명)`
          : '다음 호출: -',
      });
    } catch (_) {}
  }, [boot.shop_id]);

  const showMenu = useCallback(async (tableId, categoryId = 0) => {
    const box = boot.tables.find((t) => Number(t.id) === Number(tableId));
    const tableName = box?.name || '';

    setSelectedTableId(Number(tableId));
    setSelectedTableName(tableName);
    setPanelMode('menu');
    setCurrentOrderId(0);
    setCurrentOrderDate('');
    setPanelTitle(tableName);
    updatePanelFooter(Number(tableId), tableName);

    setMenuData((prev) => ({
      ...prev,
      loading: true,
      error: '',
      shop_id: boot.shop_id,
      table_id: Number(tableId),
      shop: prev.shop,
      table: prev.table,
      token: prev.token,
      category_id: Number(categoryId || 0),
      cats: [],
      products: [],
      cart_count: 0,
      added: 0,
      added_name: '',
    }));

    try {
      let url = `/api/pos/menu?shop_id=${encodeURIComponent(boot.shop_id)}&table_id=${encodeURIComponent(tableId)}`;
      if (Number(categoryId) > 0) {
        url += `&id=${encodeURIComponent(categoryId)}`;
      }

      const res = await fetch(url, {
        cache: 'no-store',
        credentials: 'same-origin',
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setMenuData((prev) => ({
          ...prev,
          loading: false,
          error: data.message || `메뉴 로드 실패 (${res.status})`,
        }));
        return;
      }

      setMenuData({
        loading: false,
        error: '',
        shop_id: Number(data.shop_id || 0),
        table_id: Number(data.table_id || 0),
        shop: data.shop || null,
        table: data.table || null,
        token: data.token || '',
        category_id: Number(data.category_id || 0),
        cats: Array.isArray(data.cats) ? data.cats : [],
        products: Array.isArray(data.products) ? data.products : [],
        cart_count: Number(data.cart_count || 0),
        added: Number(data.added || 0),
        added_name: data.added_name || '',
      });
    } catch (e) {
      setMenuData((prev) => ({
        ...prev,
        loading: false,
        error: e.message || '메뉴 로드 실패',
      }));
    }
  }, [boot.shop_id, boot.tables, updatePanelFooter]);

  const submitMenuProduct = useCallback(async (product, selectedOptionItems = []) => {
    if (!selectedTableId) return;

    try {
      const fd = new FormData();
      fd.append('shop_id', String(boot.shop_id));
      fd.append('table_id', String(selectedTableId));
      fd.append('product_id', String(product.id));
      fd.append('added_name', String(product.name || ''));
      fd.append('selected_option_items', JSON.stringify(selectedOptionItems));
      fd.append('ajax', '1');

      const res = await fetch('/api/pos/menu', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        alert(data.message || '장바구니 담기에 실패했습니다.');
        return;
      }

      setMenuData((prev) => ({
        ...prev,
        cart_count: Number(data.cart_count || 0),
        added: 1,
        added_name: data.added_name || '',
      }));
      setOptionModal({ open: false, product: null, selected: {} });
    } catch (e) {
      alert('네트워크 오류가 발생했습니다.');
    }
  }, [boot.shop_id, selectedTableId]);

  const addMenuProduct = useCallback((product) => {
    const groups = Array.isArray(product?.option_groups) ? product.option_groups : [];

    if (groups.length === 0) {
      submitMenuProduct(product, []);
      return;
    }

    setOptionModal({
      open: true,
      product,
      selected: {},
    });
  }, [submitMenuProduct]);

  const confirmOptionProduct = useCallback(() => {
    const product = optionModal.product;
    if (!product) return;

    const groups = Array.isArray(product.option_groups) ? product.option_groups : [];
    const selectedOptionItems = [];

    for (const group of groups) {
      const selectedItemId = Number(optionModal.selected[String(group.id)] || 0);
      const required = String(group.required || 'N').toUpperCase() === 'Y';

      if (required && selectedItemId <= 0) {
        alert(`${group.group_name || '필수 옵션'}을 선택해주세요.`);
        return;
      }

      if (selectedItemId <= 0) continue;

      const item = (group.items || []).find((row) => Number(row.id) === selectedItemId);
      if (!item) continue;

      selectedOptionItems.push({
        group_id: Number(group.id),
        item_id: Number(item.id),
        group_name: group.group_name || '',
        item_name: item.item_name || '',
        extra_price: Number(item.extra_price || 0),
      });
    }

    submitMenuProduct(product, selectedOptionItems);
  }, [optionModal, submitMenuProduct]);

  const showCart = useCallback(async (tableId) => {
    const box = boot.tables.find((t) => Number(t.id) === Number(tableId));
    const tableName = box?.name || '';

    setSelectedTableId(Number(tableId));
    setSelectedTableName(tableName);
    setPanelMode('cart');
    setCurrentOrderId(0);
    setCurrentOrderDate('');
    setPanelTitle(tableName);
    updatePanelFooter(Number(tableId), tableName);

    setCartData({
      loading: true,
      error: '',
      shop_id: Number(boot.shop_id || 0),
      table_id: Number(tableId || 0),
      items: [],
      total: 0,
      can_place: false,
    });

    try {
      const res = await fetch(`/api/pos/cart?shop_id=${encodeURIComponent(boot.shop_id)}&table_id=${encodeURIComponent(tableId)}`, {
        cache: 'no-store',
        credentials: 'same-origin',
      });

      const contentType = (res.headers.get('content-type') || '').toLowerCase();
      const text = await res.text();
      let data = null;

      if (contentType.includes('application/json')) {
        data = JSON.parse(text);
      } else {
        const trimmed = String(text || '').trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          data = JSON.parse(trimmed);
        }
      }

      if (!data) {
        setPanelHtml(text);
        return;
      }

      const payload = (data && typeof data === 'object' && data.data && typeof data.data === 'object') ? data.data : data;
      const success = typeof data.success === 'undefined' ? true : !!data.success;
      if (!res.ok || !success) {
        setCartData((prev) => ({
          ...prev,
          loading: false,
          error: data.message || `장바구니 로드 실패 (${res.status})`,
        }));
        return;
      }

      setCartData({
        loading: false,
        error: '',
        shop_id: Number(payload.shop_id || boot.shop_id || 0),
        table_id: Number(payload.table_id || tableId || 0),
        items: Array.isArray(payload.items) ? payload.items : [],
        total: Number(payload.total || 0),
        can_place: !!payload.can_place,
      });
    } catch (e) {
      setCartData((prev) => ({
        ...prev,
        loading: false,
        error: e.message || '장바구니 로드 실패',
      }));
    }
  }, [boot.shop_id, boot.tables, updatePanelFooter]);

  const postCartAction = useCallback(async (action, productId = null, cartId = null, lineId = '') => {
    const tableId = Number(selectedTableId || 0);
    if (!tableId) return false;

    const fd = new FormData();
    fd.append('action', String(action));
    if (productId !== null) fd.append('product_id', String(productId));
    if (cartId !== null && Number(cartId) > 0) {
      fd.append('cart_id', String(cartId));
    }

    let text = '';
    try {
      const res = await fetch(`/api/pos/cart?shop_id=${encodeURIComponent(boot.shop_id)}&table_id=${encodeURIComponent(tableId)}`, {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        cache: 'no-store',
      });
      text = await res.text();
      const data = JSON.parse(text);

      if (!data.success) {
        alert(data.message || '처리 실패');
        return false;
      }

      try {
        window.parent?.postMessage?.({ type: 'refresh' }, '*');
      } catch (_) {}

      await fetchStates();
      await fetchLiveOrders(false);
      await showCart(tableId);
      return true;
    } catch (e) {
      alert('AJAX 실패 또는 JSON 파싱 실패:\n' + (text || e.message));
      return false;
    }
  }, [boot.shop_id, selectedTableId, fetchStates, fetchLiveOrders, showCart]);

  const openCartFromMenu = useCallback(() => {
    if (!selectedTableId) return;
    showCart(Number(selectedTableId));
  }, [selectedTableId, showCart]);

  const buildLiveOrderDetailHtml = useCallback((data) => {
    const order = data?.order || {};
    const items = Array.isArray(data?.items) ? data.items : [];
    const delivery = data?.delivery || {};
    const pickup = data?.pickup || {};
  
    const orderId = Number(
      data?.order_id ||
      order?.id ||
      0
    );
  
    const buyerName =
      data?.displayBuyerName ||
      order?.buyer_name ||
      '';
  
    const shopName =
      data?.shop_name ||
      '';
  
    const channelLabel =
      data?.channelLabel ||
      '';
  
    const currentStatusLabel =
      data?.currentStatusLabel ||
      '';
  
    const paymentMethod =
      order?.payment_method ||
      '';
  
    const orderDate =
      order?.order_date ||
      '';
  
    const isTableOrder =
      data?.isTableOrder === true;
  
    // =====================================================
    // 금액
    // =====================================================
    const optionAmount = Number(
      data?.optionAmount ?? 0
    );
  
    const productDiscountAmount = Number(
      data?.productDiscountAmount ??
      order?.product_discount_amount ??
      0
    );
  
    const couponDiscountAmount = Number(
      data?.couponDiscountAmount ??
      order?.discount_amount ??
      0
    );
  
    const deliveryCost = Number(
      data?.deliveryCost ??
      order?.delivery_cost ??
      0
    );
  
    const discountedItemAmount = Number(
      data?.discountedItemAmount ??
      data?.itemTotal ??
      order?.total_price ??
      0
    );
  
    const productAmount =
    discountedItemAmount
    + productDiscountAmount
    - optionAmount;

    // 반드시 실제 결제금액 우선
    const totalPrice = Number(
      data?.resultPrice ??
      order?.result_price ??
      (
        discountedItemAmount
        - couponDiscountAmount
        + deliveryCost
      )
    );
  
    const deliveryStatus =
      data?.delivery_status_now ||
      delivery?.delivery_status ||
      '';
  
    const pickupStatus =
      data?.pickup_status ||
      '';
  
    const pickupDatetime =
      data?.pickup_datetime ||
      '';
  
    const address =
      data?.address ||
      delivery?.address ||
      '';
  
    const isPickup =
      !!data?.isPickup;
  
    const isDelivery =
      !!data?.isDelivery;
  
    let html = `
      <div class="p-0">
  
        <div class="card border-0">
          <div class="card-body">
  
            <div class="fw-bold mb-2">
              주문 정보
            </div>
  
            <div class="row g-2 small">
  
              <div class="col-4 text-muted">
                주문자
              </div>
  
              <div class="col-8">
                ${escapeHtml(buyerName || '-')}
                                <div class="badge text-bg-dark mb-1">
                                  ${escapeHtml(channelLabel || order?.channel || '')}
                                </div>
                  
                                <div id="currentStatusText" class="fw-bold">
                                  ${escapeHtml(currentStatusLabel || '-')}
                                </div>
              </div>
  
              <div class="col-4 text-muted">
                주문일시
              </div>
  
              <div class="col-8">
                ${escapeHtml(orderDate || '-')}
              </div>
  
              <div class="col-4 text-muted">
                결제수단
              </div>
  
              <div class="col-8">
                ${escapeHtml(paymentMethod || '-')}
              </div>
  
              <div class="col-4 text-muted">
                주문상태
              </div>
  
              <div class="col-8">
                ${escapeHtml(order?.status || '-')}
              </div>
  
            </div>
          </div>
        </div>
  
        <div class="card border-0">
          <div class="card-body">
  
            <div class="fw-bold mb-3">
              주문 상품
            </div>
  
            <div class="list-group list-group-flush">
    `;
  
    if (!items.length) {
      html += `
        <div class="text-muted small">
          상품이 없습니다.
        </div>
      `;
    } else {
      for (const item of items) {
        const imageSrc = normalizeImageSrc(
          item?.image_path
        );
  
        const quantity = Math.max(
          1,
          Number(
            item?.quantity ??
            item?.qty ??
            1
          )
        );
  
        const productPrice = Number(
          item?.product_price ??
          item?.price ??
          0
        );
  
        const optionTotal = Number(
          item?.option_total ??
          0
        );
  
        const unitPrice = Number(
          item?.unit_price ??
          item?.price ??
          0
        );
  
        const lineTotal = Number(
          item?.line_total ??
          item?.line ??
          (
            unitPrice *
            quantity
          )
        );
  
        html += `
          <div class="list-group-item px-0">
  
            <div class="d-flex align-items-center gap-3">
  
              ${
                imageSrc
                  ? `
                    <img
                      src="${escapeHtml(imageSrc)}"
                      alt=""
                      style="
                        width:56px;
                        height:56px;
                        object-fit:cover;
                        border-radius:10px;
                        background:#f1f3f5;
                      "
                    />
                  `
                  : `
                    <div
                      class="d-flex align-items-center justify-content-center text-muted small"
                      style="
                        width:56px;
                        height:56px;
                        border-radius:10px;
                        background:#f1f3f5;
                      "
                    >
                      IMG
                    </div>
                  `
              }
  
              <div class="flex-grow-1">
  
                <div class="fw-bold">
                  ${escapeHtml(
                    item?.product_name ||
                    item?.name ||
                    ''
                  )}
                </div>
  
                ${buildOptionsHtml(item)}
  
                <div class="small text-muted mt-1">
                  수량 : ${quantity}
                </div>
  
              </div>
  
              <div class="fw-bold align-self-start">
                ${lineTotal.toLocaleString()}원
              </div>
  
            </div>
          </div>
        `;
      }
    }
  
    html += `
            </div>
  
            <div class="mt-3 pt-3 border-top small">
  
              <div class="d-flex justify-content-between mb-1">
                <span class="text-muted">
                  상품금액
                </span>
  
                <span>
                  ${productAmount.toLocaleString()}원
                </span>
              </div>
  
              <div class="d-flex justify-content-between mb-1">
                <span class="text-muted">
                  옵션금액
                </span>
  
                <span>
                  +${optionAmount.toLocaleString()}원
                </span>
              </div>
  
              <div class="d-flex justify-content-between mb-1">
                <span class="text-muted">
                  상품할인
                </span>
  
                <span class="${
                  productDiscountAmount > 0
                    ? 'text-danger'
                    : ''
                }">
                  ${
                    productDiscountAmount > 0
                      ? '-'
                      : ''
                  }${productDiscountAmount.toLocaleString()}원
                </span>
              </div>
  
              <div class="d-flex justify-content-between mb-1">
                <span class="text-muted">
                  쿠폰할인
                </span>
  
                <span class="${
                  couponDiscountAmount > 0
                    ? 'text-danger'
                    : ''
                }">
                  ${
                    couponDiscountAmount > 0
                      ? '-'
                      : ''
                  }${couponDiscountAmount.toLocaleString()}원
                </span>
              </div>
  
              <div class="d-flex justify-content-between mb-1">
                <span class="text-muted">
                  배송료
                </span>
  
                <span>
                  +${deliveryCost.toLocaleString()}원
                </span>
              </div>
  
            </div>
  
            <div class="d-flex justify-content-between align-items-center mt-2 pt-3 border-top">
  
              <div class="fw-bold">
                최종 결제금액
              </div>
  
              <div class="fw-bold fs-5">
                ${totalPrice.toLocaleString()}원
              </div>
  
            </div>
  
          </div>
        </div>
    `;
  
    html += `
      <div class="card border-0">
        <div class="card-body">
  
          <div class="fw-bold mb-2">
            수령 / 배송 정보
          </div>
  
          <div class="row g-2 small">
  
            <div class="col-4 text-muted">
              구분
            </div>
  
            <div class="col-8">
              ${
                isPickup
                  ? '픽업'
                  : isDelivery
                    ? '배달'
                    : '-'
              }
            </div>
    `;
  
    if (isPickup) {
      html += `
        <div class="col-4 text-muted">
          픽업상태
        </div>
  
        <div class="col-8">
          ${escapeHtml(pickupStatus || '-')}
        </div>
  
        <div class="col-4 text-muted">
          픽업시간
        </div>
  
        <div class="col-8">
          ${escapeHtml(
            pickupDatetime ||
            pickup?.pickup_datetime ||
            '-'
          )}
        </div>
      `;
    }
  
    if (isDelivery) {
      html += `
        <div class="col-4 text-muted">
          배송상태
        </div>
  
        <div class="col-8">
          ${escapeHtml(deliveryStatus || '-')}
        </div>
  
        <div class="col-4 text-muted">
          주소
        </div>
  
        <div class="col-8">
          ${escapeHtml(address || '-')}
        </div>
      `;
    }
  
    if (!isPickup && !isDelivery) {
      html += `
        <div class="col-4 text-muted">
          주소
        </div>
  
        <div class="col-8">
          ${escapeHtml(address || '-')}
        </div>
      `;
    }
  
    html += `
          </div>
        </div>
      </div>
  
      <div class="card border-0">
        <div class="card-body">
  
          <div class="fw-bold mb-2">
            상태 변경
          </div>
  
          <div class="d-flex flex-wrap gap-2">
  
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              onclick="window.setStatus('접수')"
            >
              접수
            </button>
  
            <button
              type="button"
              class="btn btn-outline-warning btn-sm"
              onclick="window.setStatus('준비중')"
            >
              준비중
            </button>
  
            <button
              type="button"
              class="btn btn-outline-success btn-sm"
              onclick="window.setStatus('완료')"
            >
              완료
            </button>
  
            <button
              type="button"
              class="btn btn-outline-danger btn-sm"
              onclick="window.setStatus('취소')"
            >
              취소
            </button>
  
          </div>
        </div>
      </div>
  
    </div>
    `;
  
    return html;
  }, []);

  const showLiveOrderDetail = useCallback(async (orderId) => {
    setPanelTitle(`주문 상세 #${orderId}`);
    setPanelMode('live-order-detail');
    setSelectedTableId(null);
    setSelectedTableName('');
    setCurrentOrderId(Number(orderId || 0));
    setCurrentOrderDate('');
    setPanelHint('상태/금액이 여기에도 표시됩니다.');
    setPanelHtml('<div class="p-3 text-muted">불러오는 중...</div>');

    try {
      const qs = new URLSearchParams();
      qs.set('order_id', String(orderId));

      for (const [k, v] of searchParams.entries()) {
        if (k === 'order_id') continue;
        if (!qs.has(k)) qs.set(k, v);
      }

      const path = `/api/pos/admin-kiosk-order-view?${qs.toString()}`;

      const res = await fetch(path, {
        credentials: 'same-origin',
        cache: 'no-store',
      });

      const contentType = (res.headers.get('content-type') || '').toLowerCase();
      const text = await res.text();

      if (contentType.includes('application/json')) {
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          setPanelHtml(`<div class="p-3 text-danger">JSON 파싱 오류: ${escapeHtml(e.message)}</div>`);
          return;
        }

        if (!data.success) {
          setPanelHtml(`<div class="p-3 text-danger">${escapeHtml(data.message || '조회 실패')}</div>`);
          return;
        }

        setCurrentOrderId(Number(data.order_id || data?.order?.id || 0));
        setCurrentOrderDate(String(data?.order?.order_date || '').slice(0, 10) || '');
        setPanelHtml(buildLiveOrderDetailHtml(data));
        return;
      }

      const trimmed = String(text || '').trim();

      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const data = JSON.parse(trimmed);

          if (!data.success) {
            setPanelHtml(`<div class="p-3 text-danger">${escapeHtml(data.message || '조회 실패')}</div>`);
            return;
          }

          setCurrentOrderId(Number(data.order_id || data?.order?.id || 0));
          setCurrentOrderDate(String(data?.order?.order_date || '').slice(0, 10) || '');
          setPanelHtml(buildLiveOrderDetailHtml(data));
          return;
        } catch (_) {
          // 그대로 아래 HTML 처리
        }
      }

      setPanelHtml(text);
    } catch (e) {
      setPanelHtml(`<div class="p-3 text-danger">네트워크/스크립트 오류: ${escapeHtml(e.message)}</div>`);
    }
  }, [buildLiveOrderDetailHtml, escapeHtml, searchParams]);

  const showOrderDetail = useCallback(async (tableId) => {
    const box = boot.tables.find((t) => Number(t.id) === Number(tableId));
    const tableName = box?.name || '';

    setSelectedTableId(Number(tableId));
    setSelectedTableName(tableName);
    setPanelMode('detail');
    setPanelTitle(`${tableName} · 주문내역`);
    updatePanelFooter(Number(tableId), tableName);
    setPanelHtml('<div class="p-3 text-muted">불러오는 중...</div>');

    try {
      const res = await fetch(`/api/pos/table-order-detail-api?table_id=${tableId}`, {
        cache: 'no-store',
        credentials: 'same-origin',
      });

      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        setPanelHtml(
          `<div class="p-3"><div class="text-danger fw-bold mb-2">상세 API가 JSON을 반환하지 않음</div><pre class="mt-2 small bg-light p-2" style="white-space:pre-wrap;">${escapeHtml(text).slice(0, 2000)}</pre></div>`
        );
        setCurrentOrderId(0);
        setCurrentOrderDate('');
        return;
      }

      const data = await res.json();
      if (!data.success) {
        setPanelHtml(`<div class="p-3 text-danger">${escapeHtml(data.message || '조회 실패')}</div>`);
        setCurrentOrderId(0);
        setCurrentOrderDate('');
        return;
      }

      setCurrentOrderId(Number(data.order_id || 0));
      setCurrentOrderDate(String(data.order_date || '').slice(0, 10) || getLocalYmd());

      const sections = Array.isArray(data.sections) ? data.sections : [];
      const st = stateMap[String(Number(tableId))] || {};
      const stateHasAddon = !!st.has_addon;

      if (sections.length === 0) {
        setPanelHtml('<div class="p-3 text-muted">주문 내역이 없습니다.</div>');
        return;
      }

      const isFirstUnackedOrder =
        !!data.order_id &&
        stateHasAddon === true &&
        sections.length === 1 &&
        String(sections[0].key || '') === 'base';

      let html = '';
      if (data.order_id) {
        html += `<div class="mb-2"><span class="badge text-bg-dark">주문번호</span> ${escapeHtml(data.order_id)}</div>`;
      } else {
        html += '<div class="mb-2"><span class="badge text-bg-secondary">결제 전 카트</span></div>';
      }

      for (const sec of sections) {
        const isAddon = sec.key === 'addon';
        const rawItems = Array.isArray(sec.items) ? sec.items : [];

        // 기존주문(base) 상품만 화면 표시용으로 합산한다.
        // 같은 상품 + 같은 옵션 조합 + 같은 단가일 때만 하나로 합친다.
        // 추가주문(addon)은 원본 순서와 항목을 그대로 유지한다.
        let items = rawItems;

        if (sec.key === 'base') {
          const groupedItemMap = new Map();
          const groupedItems = [];

          for (const rawItem of rawItems) {
            const productId = Number(
              rawItem?.product_id || 0
            );

            const optionTotal = Number(
              rawItem?.option_total || 0
            );

            const productPrice = Number(
              rawItem?.product_price ??
              Math.max(
                0,
                Number(rawItem?.price || 0) - optionTotal
              )
            );

            const unitPrice = Number(
              rawItem?.unit_price ??
              rawItem?.price ??
              0
            );

            const optionSignature = getItemOptions(rawItem)
              .map((option) => [
                Number(
                  option?.option_group_id ??
                  option?.group_id ??
                  0
                ),
                Number(
                  option?.option_item_id ??
                  option?.item_id ??
                  0
                ),
                getOptionPrice(option),
              ])
              .sort((a, b) => {
                if (a[0] !== b[0]) return a[0] - b[0];
                if (a[1] !== b[1]) return a[1] - b[1];
                return a[2] - b[2];
              })
              .map((row) => row.join(':'))
              .join('|');

            const groupKey = [
              productId,
              productPrice,
              unitPrice,
              optionSignature,
            ].join('::');

            const qty = Number(
              rawItem?.qty ??
              rawItem?.quantity ??
              0
            );

            const line = Number(
              rawItem?.line ??
              rawItem?.line_total ??
              (unitPrice * qty)
            );

            if (groupedItemMap.has(groupKey)) {
              const groupedItem = groupedItemMap.get(groupKey);

              groupedItem.qty =
                Number(groupedItem.qty || 0) + qty;

              groupedItem.quantity =
                Number(groupedItem.quantity || 0) + qty;

              groupedItem.line =
                Number(groupedItem.line || 0) + line;

              groupedItem.line_total =
                Number(groupedItem.line_total || 0) + line;
            } else {
              const groupedItem = {
                ...rawItem,
                qty,
                quantity: qty,
                line,
                line_total: line,
              };

              groupedItemMap.set(groupKey, groupedItem);
              groupedItems.push(groupedItem);
            }
          }

          items = groupedItems;
        }

        const secTotal = Number(sec.total || 0);
        const treatAsNewFirst = isFirstUnackedOrder && sec.key === 'base';
        const titleText = treatAsNewFirst ? '' : String(sec.title || '');
        const titleHtml = titleText
          ? `${escapeHtml(titleText)}${isAddon ? '<span class="badge text-bg-danger ms-2">NEW</span>' : ''}`
          : `${treatAsNewFirst ? '신규 주문 <span class="badge text-bg-danger">NEW</span>' : ''}`;

        html += `<div class="mt-3"><div class="d-flex align-items-center justify-content-between mb-2"><div class="fw-bold ${(isAddon || treatAsNewFirst) ? 'text-danger' : ''}">${titleHtml}</div><div class="fw-bold">${secTotal.toLocaleString()}원</div></div><div class="list-group">`;

        for (const it of items) {
          const price = Number(it.price || 0);
          const qty = Number(it.qty || 0);
          const line = Number(it.line || 0);
          const redBorder = (isAddon || treatAsNewFirst) ? 'border border-danger-subtle' : '';

          const optionTotal = Number(it.option_total || 0);
          const productPrice = Number(it.product_price ?? Math.max(0, price - optionTotal));

          html += `
            <div class="list-group-item d-flex justify-content-between align-items-center ${redBorder}">
              <div>
                <div class="fw-bold">${escapeHtml(it.name)}</div>
                ${buildOptionsHtml(it)}
                <div class="text-muted small mt-1">
                  수량 ${qty}
                </div>
              </div>
              <div class="fw-bold align-self-start">${line.toLocaleString()}원</div>
            </div>
          `;
        }

        html += '</div></div>';
      }

      const grand = Number(data.grand_total || 0);
      html += `<div class="d-flex justify-content-between mt-3"><div class="fw-bold">총 합계</div><div class="fw-bold">${grand.toLocaleString()}원</div></div>`;
      setPanelHtml(`<div class="p-3">${html}</div>`);
      updatePanelFooter(Number(tableId), tableName);
    } catch (e) {
      setPanelHtml(`<div class="p-3 text-danger">네트워크/스크립트 오류: ${escapeHtml(e.message)}</div>`);
      setCurrentOrderId(0);
      setCurrentOrderDate('');
    }
  }, [boot.tables, stateMap, updatePanelFooter]);

  const placeCartOrder = useCallback(async () => {
    if (!cartData.can_place) return;
    if (!window.confirm('주문을 전송할까요?\n결제는 나갈 때 카운터에서 진행합니다.')) return;

    let text = '';
    try {
      const res = await fetch('/api/pos/table-place-order', {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
      });
      text = await res.text();
      const data = JSON.parse(text);

      if (!data.success) {
        alert(data.message || '주문 전송 실패');
        return;
      }

      alert('주문이 전송되었습니다!');

      await fetchStates();
      if (selectedTableId) {
        setTimeout(() => {
          showOrderDetail(Number(selectedTableId));
        }, 150);
      }
    } catch (e) {
      alert('주문 전송 실패(서버응답 확인):\n' + (text || e.message));
    }
  }, [cartData.can_place, fetchStates, selectedTableId, showOrderDetail]);

  const showHistory = useCallback(async () => {
    if (!selectedTableId || !currentOrderId) return;

    setPanelMode('history');
    setPanelTitle(`${selectedTableName} · 주문이력`);
    updatePanelFooter();
    setPanelHtml('<div class="p-3 text-muted">불러오는 중...</div>');

    try {
      const res = await fetch(
        `/api/pos/api/order-addon-list?order_id=${encodeURIComponent(currentOrderId)}&limit=50`,
        { cache: 'no-store', credentials: 'same-origin' }
      );

      const ct = (res.headers.get('content-type') || '').toLowerCase();
      if (!ct.includes('application/json')) {
        const text = await res.text();
        setPanelHtml(
          `<div class="p-3"><div class="text-danger fw-bold mb-2">추가주문 이력 API가 JSON을 반환하지 않음</div><div class="text-muted small">/pos/api/order-addon-list 확인</div><pre class="mt-2 small bg-light p-2" style="white-space:pre-wrap;">${escapeHtml(text).slice(0, 2000)}</pre></div>`
        );
        return;
      }

      const data = await res.json();
      if (!data.success) {
        setPanelHtml(`<div class="p-3 text-danger">${escapeHtml(data.message || '조회 실패')}</div>`);
        return;
      }

      const addons = Array.isArray(data.addons) ? data.addons : [];
      let html = `<div class="p-3"><div class="fw-bold mb-3">주문번호 ${escapeHtml(data.order_id || currentOrderId)}</div>`;

      if (addons.length === 0) {
        html += '<div class="text-muted">추가주문 이력 없음</div>';
      } else {
        for (const a of addons) {
          const items = Array.isArray(a.items) ? a.items : [];
          html += `<div class="mb-3"><div class="mb-2"><span class="ms-2">${escapeHtml(a.created_at || '')}</span></div>`;
          if (a.note) html += `<div class="small text-muted mb-2">${escapeHtml(a.note)}</div>`;
          html += '<div class="table-responsive"><table class="table table-sm mb-0"><thead><tr><th style="width:140px">상품명</th><th style="width:80px" class="text-end">수량</th><th style="width:120px" class="text-end">단가</th><th style="width:140px" class="text-end">금액</th></tr></thead><tbody>';

          if (items.length === 0) {
            html += '<tr><td colSpan="4" class="text-muted">아이템 없음</td></tr>';
          } else {
            for (const it of items) {
              html += `
                <tr>
                  <td>
                    <div>${escapeHtml(it.name)}</div>
                    ${buildOptionsHtml(it)}
                  </td>
                  <td class="text-end">${Number(it.qty || 0).toLocaleString()}</td>
                  <td class="text-end">${Number(it.price || 0).toLocaleString()}원</td>
                  <td class="text-end fw-bold">${Number(it.line || 0).toLocaleString()}원</td>
                </tr>
              `;
            }
          }

          html += '</tbody></table></div></div>';
        }
      }

      html += '</div>';
      setPanelHtml(html);
    } catch (e) {
      setPanelHtml(`<div class="p-3 text-danger">네트워크/스크립트 오류: ${escapeHtml(e.message)}</div>`);
    }
  }, [currentOrderId, selectedTableId, selectedTableName, updatePanelFooter]);

  const handlePaid = useCallback(async () => {
    if (!selectedTableId) return;
    if (!window.confirm('결제완료 처리하고 테이블을 초기화할까요?')) return;

    const fd = new FormData();
    fd.append('table_id', String(selectedTableId));

    let res;
    let text = '';
    let data;

    try {
      res = await fetch('/api/pos/table-close', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      });
      text = await res.text();
      data = JSON.parse(text);
    } catch (e) {
      alert('서버 응답 오류:\n' + (text || e.message));
      return;
    }

    if (!data.success) {
      alert(data.message || '처리 실패');
      return;
    }

    setCurrentOrderId(0);
    setCurrentOrderDate('');
    await fetchStates();

    if (panelMode === 'detail') {
      showOrderDetail(selectedTableId);
    }
  }, [fetchStates, panelMode, selectedTableId, showOrderDetail]);

  const handleAck = useCallback(async () => {
    const tid = Number(selectedTableId);
    if (!Number.isFinite(tid) || tid <= 0) {
      alert('테이블을 먼저 선택하세요. table_id=' + selectedTableId);
      return;
    }
    if (!window.confirm('주문 확인 처리할까요? (NEW 표시가 사라집니다)')) return;

    try {
      const fdC = new FormData();
      fdC.append('table_id', String(tid));
      fdC.append('created_by', 'pos');

      const rC = await fetch('/api/pos/table-addon-commit', {
        method: 'POST',
        body: fdC,
        credentials: 'same-origin',
      });
      const tC = await rC.text();

      let dC;
      try {
        dC = JSON.parse(tC);
      } catch (_) {
        alert('commit 응답이 JSON이 아님:\n' + tC.slice(0, 800));
        return;
      }

      if (!dC.success) {
        alert(dC.message || 'commit 실패');
        return;
      }
    } catch (e) {
      alert('주문 이력 저장(commit) 실패:\n' + e.message);
      return;
    }

    try {
      const fd = new FormData();
      fd.append('table_id', String(tid));

      const r = await fetch('/api/pos/table-ack', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      });
      const tx = await r.text();
      const d = JSON.parse(tx);

      if (!d.success) {
        alert(d.message || 'ACK 처리 실패');
        return;
      }
    } catch (e) {
      alert('ACK 서버 응답 오류:\n' + e.message);
      return;
    }

    await fetchStates();

    if (panelMode === 'detail') {
      showOrderDetail(tid);
    }
  }, [fetchStates, panelMode, selectedTableId, showOrderDetail]);

  const handleKioskCounterPaid = useCallback(async () => {
    const token = String(kioskToast.token || '');
    if (!token) {
      alert('토큰(token)이 없습니다. (서버가 KIOSK_COUNTER_REQUEST에 token을 보내야 합니다)');
      return;
    }

    if (!window.confirm('카운터 결제를 결제완료로 확정하고 주문을 생성할까요?')) return;

    const fd = new FormData();
    fd.append('token', token);

    let res;
    let text = '';
    let data;

    try {
      res = await fetch('/api/pos/kiosk-counter-paid', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      });
      text = await res.text();
      data = JSON.parse(text);
    } catch (e) {
      alert('서버 응답 오류:\n' + (text || e.message));
      return;
    }

    if (!data.success) {
      alert(data.message || '처리 실패');
      return;
    }

    alert('결제완료 처리됨! 주문번호: ' + (data.order_id || ''));
    setKioskToast({
      visible: false,
      code: '-',
      amount: '-',
      itemsHtml: '',
      token: '',
    });

    await fetchStates();

    if (data.table_id) {
      showOrderDetail(parseInt(data.table_id, 10));
    }
  }, [fetchStates, kioskToast.token, showOrderDetail]);

  useEffect(() => {
    orderSoundRef.current = new Audio('/pos/sounds/order.mp3');
    orderSoundRef.current.preload = 'auto';
    loadBootstrap();
  }, [loadBootstrap]);

  useEffect(() => {
    document.body.classList.add('pos-body');
    return () => document.body.classList.remove('pos-body');
  }, []);

  useEffect(() => {
    if (boot.loading || boot.error || !boot.shop_id) return;
    if (initialLoadDoneRef.current) return;

    initialLoadDoneRef.current = true;
    fetchStates();
    fetchLiveOrders(false);
    loadWaitingSummary();
    applyScale();
  }, [applyScale, boot.error, boot.loading, boot.shop_id, fetchLiveOrders, fetchStates, loadWaitingSummary]);

  useEffect(() => {
    updatePanelFooter();
  }, [stateMap, selectedTableId, selectedTableName, updatePanelFooter]);

  useEffect(() => {
    const onResize = () => applyScale();
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onResize);
    };
  }, [applyScale]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      setPushStatus({
        loading: false,
        supported: false,
        permission: 'unsupported',
        subscribed: false,
        message: '서비스워커를 지원하지 않는 브라우저입니다.',
      });
      return;
    }

    navigator.serviceWorker
      .register('/pos/sw_pos.js', {
        scope: '/pos/',
      })
      .then((registration) => {
        console.log(
          'POS 서비스워커 등록 성공:',
          registration.scope
        );

        checkPushStatus();
      })
      .catch((error) => {
        console.error(
          'POS 서비스워커 등록 실패:',
          error
        );

        setPushStatus({
          loading: false,
          supported: false,
          permission: 'error',
          subscribed: false,
          message: '서비스워커 등록에 실패했습니다.',
        });
      });
  }, [checkPushStatus]);

  useEffect(() => {
    if (!boot.shop_id) return;

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${proto}://${window.location.host}/ws/?shop_id=${boot.shop_id}`;

    const refreshSoon = () => {
      if (pendingRef.current) return;
      pendingRef.current = true;
      setTimeout(() => {
        pendingRef.current = false;
        fetchStates();
        fetchLiveOrders(false, liveStatus, livePage, { silent: true });
      }, 150);
    };

    const connect = () => {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        retryRef.current = 0;
        loadWaitingSummary();
      };

      wsRef.current.onmessage = (event) => {
        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch (_) {
          return;
        }

        if (msg.type === 'waiting' && Number(msg.shop_id) === Number(boot.shop_id)) {
          loadWaitingSummary();
          return;
        }

        if (
          msg.type === 'NEW_ORDER' ||
          msg.type === 'ORDER_STATUS_CHANGED' ||
          msg.type === 'TABLE_UPDATED' ||
          msg.type === 'TABLE_STATE'
        ) {
          refreshSoon();
          if (msg.type === 'NEW_ORDER') fetchLiveOrders(true, liveStatus, livePage, { silent: true });
          return;
        }

        if (msg.type === 'KIOSK_COUNTER_REQUEST') {
          const body = msg.data || {};
        
          const items = Array.isArray(body.items)
            ? body.items
            : [];
        
          const code =
            body.code ||
            (
              body.request_id
                ? `K${body.request_id}`
                : '-'
            );
        
          let calculatedAmount = 0;
        
          const itemsHtml = items.length > 0
            ? `
              <div class="fw-bold mb-2">
                메뉴
              </div>
        
              <div>
                ${items.map((it) => {
                  const quantity = Math.max(
                    1,
                    Number(
                      it?.quantity ??
                      it?.qty ??
                      1
                    )
                  );
        
                  const productPrice = Number(
                    it?.display_price ??
                    it?.product_price ??
                    it?.price ??
                    0
                  );
        
                  const options = Array.isArray(it?.options)
                    ? it.options
                    : Array.isArray(it?.option_items)
                      ? it.option_items
                      : [];
        
                  const calculatedOptionTotal = options.reduce(
                    (sum, option) => {
                      return sum + Number(
                        option?.option_price ??
                        option?.extra_price ??
                        0
                      );
                    },
                    0
                  );
        
                  const optionTotal = Number(
                    it?.option_total ??
                    it?.option_price ??
                    calculatedOptionTotal
                  );
        
                  const unitPrice = Number(
                    it?.unit_price ??
                    (
                      productPrice +
                      optionTotal
                    )
                  );
        
                  const lineTotal = Number(
                    it?.line_total ??
                    it?.subtotal ??
                    it?.line ??
                    (
                      unitPrice *
                      quantity
                    )
                  );
        
                  calculatedAmount += lineTotal;
        
                  const productName =
                    it?.product_name ||
                    it?.name ||
                    '';
        
                  const optionsHtml = options.length > 0
                    ? `
                      <div class="mt-1">
                        ${options.map((option) => {
                          const groupName =
                            option?.option_group_name ||
                            option?.group_name ||
                            '';
        
                          const itemName =
                            option?.option_item_name ||
                            option?.item_name ||
                            '';
        
                          const optionPrice = Number(
                            option?.option_price ??
                            option?.extra_price ??
                            0
                          );
        
                          return `
                            <div class="small text-muted">
                              · ${
                                groupName
                                  ? `${escapeHtml(groupName)}: `
                                  : ''
                              }${escapeHtml(itemName)}
                              ${
                                optionPrice !== 0
                                  ? ` (${optionPrice > 0 ? '+' : ''}${optionPrice.toLocaleString()}원)`
                                  : ''
                              }
                            </div>
                          `;
                        }).join('')}
                      </div>
                    `
                    : '';
        
                  return `
                    <div class="py-2 border-bottom">
        
                      <div class="d-flex justify-content-between gap-3">
        
                        <div class="flex-grow-1">
        
                          <div class="fw-bold">
                            ${escapeHtml(productName)}
                            <span class="text-muted">
                              × ${quantity}
                            </span>
                          </div>
        
                          ${optionsHtml}
        
                          <div class="small text-muted mt-1">
                            상품 ${productPrice.toLocaleString()}원
                            ${
                              optionTotal !== 0
                                ? ` · 옵션 ${optionTotal > 0 ? '+' : ''}${optionTotal.toLocaleString()}원`
                                : ''
                            }
                          </div>
        
                        </div>
        
                        <div class="fw-bold text-nowrap">
                          ${lineTotal.toLocaleString()}원
                        </div>
        
                      </div>
        
                    </div>
                  `;
                }).join('')}
              </div>
            `
            : '';
        
          /*
           * 서버 amount가 정상이라면 서버 값을 사용한다.
           * 서버 amount가 없거나 0이면 POS에서 옵션 포함 합계를 계산한다.
           */
          const serverAmount = Number(
            body.amount ?? 0
          );
        
          const finalAmount =
            serverAmount > 0
              ? serverAmount
              : calculatedAmount;
        
          setKioskToast({
            visible: true,
            code: String(code),
            amount: `${finalAmount.toLocaleString()}원`,
            itemsHtml,
            token: String(body.token || ''),
          });
        
          playOrderVoice();
          return;
        }
      };

      wsRef.current.onerror = () => {
        try {
          wsRef.current.close();
        } catch (_) {}
      };

      wsRef.current.onclose = () => {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        const wait = Math.min(15000, 1000 * Math.pow(2, retryRef.current++));
        retryTimerRef.current = setTimeout(connect, wait);
      };
    };

    connect();

    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      try {
        wsRef.current?.close();
      } catch (_) {}
    };
  }, [boot.shop_id, fetchLiveOrders, fetchStates, livePage, liveStatus, loadWaitingSummary]);

  useEffect(() => {
    const onMessage = (ev) => {
      const msg = ev.data;
      if (!msg || typeof msg !== 'object') return;

      if (msg.type === 'refresh') {
        fetchStates();
        fetchLiveOrders(false, liveStatus, livePage, { silent: true });
        return;
      }
      if (msg.type === 'openDetail' && msg.table_id) {
        showOrderDetail(parseInt(msg.table_id, 10));
        return;
      }
      if (msg.type === 'openMenu' && msg.table_id) {
        showMenu(parseInt(msg.table_id, 10));
        return;
      }
      if (msg.type === 'openCart' && msg.table_id) {
        showCart(parseInt(msg.table_id, 10));
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [fetchLiveOrders, fetchStates, livePage, liveStatus, showCart, showMenu, showOrderDetail]);

  useEffect(() => {
    window.setStatus = async function(...args) {
      let orderId = 0;
      let st = '';

      if (args.length === 2) {
        orderId = Number(args[0] || 0);
        st = args[1];
      } else if (args.length === 1) {
        const kor = args[0];
        const MAP = { '접수': 'ACCEPTED', '준비중': 'COOKING', '완료': 'DONE', '취소': 'CANCELLED' };
        orderId = Number(currentOrderId || 0);
        st = MAP[kor] || kor;
      }

      if (!orderId || !st) {
        alert('주문번호 또는 상태값이 없습니다.');
        return;
      }

      if (!window.confirm(`주문 #${orderId} 상태를 ${st}(으)로 변경할까요?`)) return;

      const res = await fetch('/api/pos/admin-kiosk-order-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ order_id: orderId, current_status: st }),
        credentials: 'same-origin',
      });

      const text = await res.text();

      let data;
      try {
        data = JSON.parse(text);
      } catch (_) {
        alert('서버 응답이 JSON이 아닙니다:\n' + text);
        return;
      }

      if (!(data.ok || data.success)) {
        alert(data.msg || data.message || '상태 변경 실패');
        return;
      }

      fetchLiveOrders(false);

      if (panelMode === 'live-order-detail' && currentOrderId) {
        showLiveOrderDetail(currentOrderId);
      }
    };

    window.setDeliveryStatus = async function(ds) {
      const orderId = Number(currentOrderId || 0);
      if (!orderId) {
        alert('order_id가 없습니다.');
        return;
      }
      if (!window.confirm(`배송상태를 "${ds}"(으)로 변경할까요?`)) return;

      const res = await fetch('/api/pos/admin-delivery-status-ajax', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ order_id: orderId, delivery_status: ds }),
        credentials: 'same-origin',
      });

      const text = await res.text();

      let data;
      try {
        data = JSON.parse(text);
      } catch (_) {
        alert('서버 응답이 JSON이 아닙니다:\n' + text);
        return;
      }

      if (!(data.ok || data.success)) {
        alert(data.msg || data.message || '배송상태 변경 실패');
        return;
      }

      if (panelMode === 'live-order-detail' && currentOrderId) {
        showLiveOrderDetail(currentOrderId);
      }
    };

    return () => {
      delete window.setStatus;
      delete window.setDeliveryStatus;
    };
  }, [currentOrderId, fetchLiveOrders, panelMode, showLiveOrderDetail]);

  const hasAddon = !!getCurrentTableState().has_addon;

  const renderLiveOrders = () => {
    if (liveMeta.loading) {
      return <div className="small text-muted">불러오는 중...</div>;
    }

    if (liveMeta.error) {
      return <div className="text-danger small">{liveMeta.error}</div>;
    }

    if (!liveOrders.length) {
      return <div className="text-center text-muted py-5">오늘 주문이 없습니다.</div>;
    }

    return (
      <>
        {liveOrders.map((o) => (
          <React.Fragment key={o.id}>
            <span className="mx-1 mb-1 d-inline-block">{o.short_dt}</span>

            <div
              className={`card order-card mb-3 ${o.is_new ? 'border border-2 border-danger-subtle' : ''}`}
              style={o.is_new ? { background: '#fff7f7', cursor: 'pointer' } : { cursor: 'pointer' }}
              onClick={() => showLiveOrderDetail(Number(o.id))}
            >
              <div className="card-body py-2 mt-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="fw-bold fs-6">#{Number(o.id)}</div>

                    <span className={`badge py-2 ${o.ch_info?.class || ''}`} style={{ fontSize: '13px' }}>
                      {o.display_buyer_name}
                    </span>
                  </div>
                </div>

                <div className="d-flex justify-content-end align-items-center">
                  <span className="badge text-dark">
                    {o.ch_info?.label || ''}주문
                  </span>
                </div>
              </div>
            </div>
          </React.Fragment>
        ))}

        {liveMeta.total_pages > 1 ? (
          <div className="d-flex justify-content-center align-items-center gap-2 mt-3 mb-2 flex-wrap">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={liveMeta.page <= 1}
              onClick={() => {
                const nextPage = Math.max(1, liveMeta.page - 1);
                setLivePage(nextPage);
                fetchLiveOrders(false, liveStatus, nextPage);
              }}
            >
              이전
            </button>

            <span className="small text-muted">
              {liveMeta.page} / {liveMeta.total_pages}
            </span>

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={liveMeta.page >= liveMeta.total_pages}
              onClick={() => {
                const nextPage = Math.min(liveMeta.total_pages, liveMeta.page + 1);
                setLivePage(nextPage);
                fetchLiveOrders(false, liveStatus, nextPage);
              }}
            >
              다음
            </button>
          </div>
        ) : null}
      </>
    );
  };

  const renderMenuPanel = () => {
    if (menuData.loading) {
      return <div className="p-3 text-muted">불러오는 중...</div>;
    }

    if (menuData.error) {
      return <div className="p-3 text-danger">{menuData.error}</div>;
    }

    const cartCount = Number(menuData.cart_count || 0);

    return (
      <div
        className="pos-menu-wrap"
        style={{
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            height: '64px',
            background: '#fff',
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 14px',
            zIndex: 20,
            flexShrink: 0,
          }}
        >
          <div className="fw-bold">메뉴</div>

          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-dark btn-sm position-relative"
              type="button"
              onClick={openCartFromMenu}
            >
              장바구니
              {cartCount > 0 ? (
                <span className="ms-1">
                  <span className="badge bg-danger">{cartCount}</span>
                </span>
              ) : null}
            </button>
          </div>
        </div>

        <div
          style={{
            padding: '16px',
            paddingBottom: '90px',
            flex: 1,
            overflowY: 'auto',
            background: '#fff',
          }}
        >
          {menuData.added ? (
            <div className="alert alert-success py-2 small mb-3">
              {menuData.added_name} 담음
            </div>
          ) : null}

          <div
            className="mb-3"
            style={{
              overflowX: 'auto',
              whiteSpace: 'nowrap',
            }}
          >
            {(menuData.cats || []).map((cat) => {
              const active = Number(menuData.category_id) === Number(cat.id);
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => showMenu(selectedTableId, Number(cat.id))}
                  style={{
                    display: 'inline-block',
                    padding: '8px 10px',
                    marginRight: '6px',
                    borderRadius: '999px',
                    textDecoration: 'none',
                    fontWeight: 700,
                    fontSize: '14px',
                    border: active ? '1px solid #111' : '1px solid #e9ecef',
                    background: active ? '#111' : '#fff',
                    color: active ? '#fff' : '#111',
                  }}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>

          {!menuData.products.length ? (
            <div className="text-muted small">상품이 없습니다.</div>
          ) : (
            <div className="row row-cols-2 row-cols-md-4 g-3">
              {menuData.products.map((p) => {
                const imageSrc = normalizeImageSrc(p.image_path);

                return (
                  <div className="col" key={p.id}>
                    <button
                      type="button"
                      className="w-100 text-start p-0 border-0 bg-transparent"
                      style={{
                        WebkitTapHighlightColor: 'transparent',
                      }}
                      onClick={() => addMenuProduct(p)}
                    >
                      <div
                        className="card"
                        style={{
                          border: 0,
                          borderRadius: '0px',
                          overflow: 'hidden',
                        }}
                      >
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt=""
                            style={{
                              width: '100%',
                              aspectRatio: '4 / 3',
                              objectFit: 'cover',
                              background: '#f1f3f5',
                            }}
                          />
                        ) : (
                          <div
                            className="d-flex align-items-center justify-content-center text-muted small"
                            style={{
                              width: '100%',
                              aspectRatio: '4 / 3',
                              background: '#f1f3f5',
                            }}
                          >
                            NO IMAGE
                          </div>
                        )}

                        <div className="pt-2">
                          <div
                            className="fw-bold"
                            style={{ fontSize: '14px', lineHeight: 1.2 }}
                          >
                            {p.name}
                          </div>
                          {(() => {
                            const originalPrice = getOriginalPrice(p);
                            const displayPrice = getDisplayPrice(p);
                            const isSale = Boolean(p.is_sale) || (
                              originalPrice > 0 &&
                              displayPrice > 0 &&
                              displayPrice < originalPrice
                            );

                            return (
                              <div className="mt-1">
                                {isSale ? (
                                  <div
                                    className="text-muted small"
                                    style={{ textDecoration: 'line-through' }}
                                  >
                                    {originalPrice.toLocaleString()}원
                                  </div>
                                ) : null}

                                <div className={isSale ? 'fw-bold text-danger' : 'text-muted fw-bold'}>
                                  {displayPrice.toLocaleString()}원
                                </div>

                                {Array.isArray(p.option_groups) && p.option_groups.length > 0 ? (
                                  <div className="small text-primary mt-1">
                                    옵션 선택
                                  </div>
                                ) : null}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={{
            position: 'sticky',
            left: 0,
            right: 0,
            bottom: 0,
            background: '#fff',
            borderTop: '1px solid #eee',
            padding: '10px',
            zIndex: 30,
            flexShrink: 0,
          }}
        >
          <div className="d-flex">
            <button
              className="btn btn-dark w-100"
              type="button"
              onClick={openCartFromMenu}
            >
              주문/전송
              {cartCount > 0 ? (
                <span className="badge bg-light text-dark ms-2">
                  {cartCount}개
                </span>
              ) : null}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCartPanel = () => {
    if (cartData.loading) {
      return <div className="p-3 text-muted">불러오는 중...</div>;
    }

    if (cartData.error) {
      return <div className="p-3 text-danger">{cartData.error}</div>;
    }

    const items = Array.isArray(cartData.items) ? cartData.items : [];

    return (
      <div style={{ background: '#f6f7f9', minHeight: '100%', position: 'relative' }}>
        <div className="container py-3" style={{ paddingBottom: '120px' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <div className="fw-bold">장바구니</div>
            </div>

            <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => showMenu(selectedTableId)}>
              메뉴
            </button>
          </div>

          {!cartData.can_place ? (
            <div className="alert alert-warning">장바구니가 비었습니다. 메뉴에서 담아주세요.</div>
          ) : (
            <div className="card mb-3" style={{ border: 0, borderRadius: '14px' }}>
              <div className="card-body">
                <div className="fw-bold mb-2">주문 추가</div>

                {items.map((it, idx) => {
                  const imageSrc = normalizeImageSrc(it.image_path);
                  return (
                    <div className="d-flex gap-3 align-items-center py-2 border-top" key={`${it.product_id || idx}-${idx}`}>
                      {imageSrc ? (
                        <img
                          className="thumb"
                          src={imageSrc}
                          alt=""
                          style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '10px', background: '#eee' }}
                        />
                      ) : (
                        <div className="thumb" style={{ width: '72px', height: '72px', borderRadius: '10px', background: '#eee' }}></div>
                      )}

                      <div className="flex-grow-1">
                        <div className="fw-bold">{it.name}</div>

                        {getItemOptions(it).length > 0 ? (
                          <div className="mt-1">
                            {getItemOptions(it).map((opt, optIdx) => {
                              const optionName = getOptionName(opt);
                              const optionPrice = getOptionPrice(opt);

                              return (
                                <div
                                  className="text-muted small"
                                  key={`${it.cart_id || it.product_id}-${optIdx}`}
                                >
                                  · {optionName}
                                  {optionPrice !== 0
                                    ? ` (${optionPrice > 0 ? '+' : ''}${optionPrice.toLocaleString()}원)`
                                    : ''}
                                </div>
                              );
                            })}
                          </div>
                        ) : null}

                        {(() => {
                          const originalPrice = getOriginalPrice(it);
                          const displayPrice = getDisplayPrice(it);
                          const optionTotal = Number(it.option_price ?? it.option_total ?? 0);
                          const unitPrice = getUnitPrice(it);
                          const isSale = Boolean(it.is_sale) || (
                            originalPrice > 0 &&
                            displayPrice > 0 &&
                            displayPrice < originalPrice
                          );

                          return (
                            <div className="mt-1">
                              {isSale ? (
                                <div
                                  className="text-muted small"
                                  style={{ textDecoration: 'line-through' }}
                                >
                                  정상가 {originalPrice.toLocaleString()}원
                                </div>
                              ) : null}

                              <div className="text-muted small">
                                상품 {displayPrice.toLocaleString()}원
                                {optionTotal > 0
                                  ? ` + 옵션 ${optionTotal.toLocaleString()}원`
                                  : ''}
                              </div>

                              <div className="fw-semibold small">
                                단가 {unitPrice.toLocaleString()}원
                              </div>
                            </div>
                          );
                        })()}

                        <div className="d-flex align-items-center gap-2 mt-2">
                          <button
                            className="btn btn-outline-secondary qty-btn"
                            type="button"
                            onClick={() => postCartAction(
                              'dec',
                              parseInt(it.product_id || 0, 10),
                              parseInt(it.cart_id || 0, 10),
                              String(it.line_id || '')
                            )}
                          >
                            -
                          </button>
                          <div className="fw-bold">{parseInt(it.qty || 0, 10)}</div>
                          <button
                            className="btn btn-outline-secondary qty-btn"
                            type="button"
                            onClick={() => postCartAction(
                              'inc',
                              parseInt(it.product_id || 0, 10),
                              parseInt(it.cart_id || 0, 10),
                              String(it.line_id || '')
                            )}
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="text-end">
                        <div className="fw-bold mb-2">
                          {Number(
                            it.line_total ??
                            it.line ??
                            (getUnitPrice(it) * Number(it.qty || 0))
                          ).toLocaleString()}원
                        </div>
                        <button
                          className="btn btn-outline-danger btn-sm ms-auto"
                          type="button"
                          onClick={() => postCartAction(
                            'remove',
                            parseInt(it.product_id || 0, 10),
                            parseInt(it.cart_id || 0, 10),
                            String(it.line_id || '')
                          )}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div className="d-flex justify-content-between mt-3">
                  <div className="fw-bold">합계</div>
                  <div className="fw-bold fs-5">{Number(cartData.total || 0).toLocaleString()}원</div>
                </div>

                <div className="d-flex justify-content-end mt-2">
                  <button className="btn btn-outline-danger btn-sm" type="button" onClick={() => {
                    if (!window.confirm('장바구니를 전부 비울까요?')) return;
                    postCartAction('clear');
                  }}>
                    전체 비우기
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ position: 'sticky', left: 0, right: 0, bottom: 0, zIndex: 20, background: '#fff', borderTop: '1px solid #eee' }}>
          <div className="container py-2">
            <button className={`btn btn-success w-100 ${cartData.can_place ? '' : 'disabled'}`} type="button" onClick={placeCartOrder}>
              주문 전송
            </button>
            <div className="text-muted small mt-1"></div>
          </div>
        </div>
      </div>
    );
  };

  if (boot.loading) return <div className="p-3">로딩 중...</div>;
  if (boot.error) return <div className="p-3 text-danger">{boot.error}</div>;

  return (
    <>
      <style>{`
        html, body{ height:100%; margin:0; }
        body.pos-body{ background:#fff; overflow:hidden; }
        .pos-page .container-fluid{ height:100vh; display:flex; flex-direction:column; }
        .pos-page .layout{ display:grid; grid-template-columns:300px 1fr 500px; gap:8px; align-items:stretch; height:calc(100vh - 110px); flex:1; min-height:0; }
        .pos-page .layout > div{ height:100%; min-height:0; }
        .pos-page .floor{ position:relative; width:100%; max-width:1200px; margin:0 auto; background:#f8f9fa; border:1px solid #e5e7eb; border-radius:12px; overflow:auto; display:flex; align-items:center; justify-content:center; height:100%; }
        .pos-page .floor-center{ min-width:100%; min-height:100%; display:flex; align-items:center; justify-content:center; padding:0; }
        .pos-page .floor-inner{ position:relative; transform-origin:center center; background-size:cover; background-position:center; background-repeat:no-repeat; flex:0 0 auto; }
        .pos-page .tbox{ position:absolute; border-radius:12px; border:1px solid rgba(0,0,0,.12); box-shadow:0 2px 8px rgba(0,0,0,.06); display:flex; flex-direction:column; align-items:center; justify-content:center; cursor:pointer; user-select:none; padding:8px 6px 28px 6px; background:#e9ecef; gap:3px; text-align:center; overflow:hidden; transition:box-shadow .15s ease, transform .15s ease, border-color .15s ease, background-color .15s ease; }
        .pos-page .tname{font-weight:800;font-size:14px;line-height:1.1}
        .pos-page .tmeta{font-size:12px;opacity:.85;line-height:1.1}
        .pos-page .badge-mini{font-size:11px}
        .pos-page .st-new{background:#ffe3e3;border-color:rgba(220,53,69,.45)}
        .pos-page .st-idle{background:#e9ecef;}
        .pos-page .addon-badge{position:absolute;top:6px;right:6px;font-size:11px;padding:.25em .5em;border-radius:999px;z-index:3}
        .pos-page .blink{animation:blink 1s infinite}
        @keyframes blink{0%{opacity:1}50%{opacity:.2}100%{opacity:1}}
        @keyframes tablePulse{
          0%{box-shadow:0 0 0 0 rgba(220,53,69,.35),0 2px 8px rgba(0,0,0,.06);border-color:rgba(220,53,69,.45);transform:scale(1)}
          50%{box-shadow:0 0 0 6px rgba(220,53,69,.10),0 0 18px rgba(220,53,69,.35);border-color:rgba(220,53,69,.90);transform:scale(1.015)}
          100%{box-shadow:0 0 0 0 rgba(220,53,69,.35),0 2px 8px rgba(0,0,0,.06);border-color:rgba(220,53,69,.45);transform:scale(1)}
        }
        .pos-page .box-blink{animation:tablePulse 1s infinite}
        .pos-page .tbtn{position:absolute;bottom:6px;height:20px;padding:0 8px;border-radius:999px;font-size:11px;border:1px solid rgba(0,0,0,.12);background:rgba(255,255,255,.85);backdrop-filter:blur(4px);display:inline-flex;align-items:center;justify-content:center;cursor:pointer;z-index:2;right:6px}
        .pos-page .panel{position:sticky;top:12px;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;background:#fff;height:100%;display:flex;flex-direction:column;min-height:0;margin-right:0;}
        .pos-page .panel-head{padding:10px 12px;border-bottom:1px solid #eee;display:flex;align-items:center;justify-content:space-between;gap:10px}
        .pos-page .panel-body{flex:1;min-height:0;overflow:auto;padding:0;background:#fafafa}
        .pos-page .panel-body iframe{width:100%;height:100%;min-height:0;border:0;border-radius:10px;background:#fff}
        .pos-page .panel-foot{border-top:1px solid #eee;padding:10px 12px;background:#fff}

        .pos-page .pos-topbar{
          display:flex;
          align-items:center;
          flex-wrap:nowrap;
          gap:12px;
          width:100%;
          min-width:0;
          white-space:nowrap;
        }

        .pos-page .pos-title{
          flex:0 0 auto;
          white-space:nowrap;
        }

        .pos-page .waiting-wrap{
          margin-left:auto;
          flex:0 0 auto;
          min-width:0;
        }

        .pos-page #waitingWidget{
          display:flex !important;
          align-items:center !important;
          flex-wrap:nowrap !important;
          width:max-content;
          min-width:max-content;
          white-space:nowrap !important;
        }

        .pos-page #waitingWidget,
        .pos-page #waitingWidget *{
          white-space:nowrap !important;
          word-break:keep-all !important;
        }
        .pos-page .live-orders-box{height:100%;display:flex;flex-direction:column;background:#fff;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;min-height:0}
        .pos-page .live-orders-head{padding:10px 10px 8px;border-bottom:1px solid #eee;background:#fff}
        .pos-page .live-orders-body{flex:1;overflow:auto;background:#fff;padding:8px;min-height:0}
        .pos-page .sound-pill{display:inline-flex;align-items:center;gap:8px;padding:6px 10px;border-radius:999px;background:#fff;border:1px solid #e5e7eb;font-size:12px;color:#6b7280;cursor:pointer}
        .pos-page .dot{width:8px;height:8px;border-radius:50%;background:#ef4444}
        .pos-page .dot.on{background:#22c55e}
        @media (max-width: 992px){
          html, body{width:100%;max-width:100%;overflow-x:hidden}
          body.pos-body{overflow:hidden}
          .pos-page{width:100%;max-width:100%;overflow:hidden;font-size:13px}
          .pos-page .container-fluid{
            width:100%;
            max-width:100%;
            height:100vh;
            min-height:100vh;
            display:flex;
            flex-direction:column;
            overflow:hidden;
            padding-top:8px !important;
            padding-bottom:8px !important;
          }

          .pos-page .pos-topbar{
            display:flex;
            align-items:center;
            justify-content:flex-start;
            flex-wrap:nowrap;
            gap:8px;
            width:100%;
            min-width:0;
            overflow-x:auto;
            overflow-y:hidden;
            white-space:nowrap;
            padding-bottom:4px;
            -webkit-overflow-scrolling:touch;
          }

          .pos-page .pos-topbar > *{
            flex:0 0 auto;
          }

          .pos-page .waiting-wrap{
            margin-left:auto;
            flex:0 0 auto;
          }

          .pos-page .pos-title{
            font-size:18px !important;
            line-height:1.15;
            min-width:72px;
            white-space:nowrap;
          }

          .pos-page #waitingWidget{
            display:flex !important;
            align-items:center !important;
            flex-wrap:nowrap !important;
            gap:8px !important;
            width:max-content;
            min-width:max-content;
            flex:0 0 auto;
            white-space:nowrap !important;
            padding:6px 10px !important;
          }

          .pos-page #waitingWidget,
          .pos-page #waitingWidget *{
            white-space:nowrap !important;
            word-break:keep-all !important;
          }

          .pos-page #waitingWidget .small{
            font-size:10px !important;
          }

          .pos-page #waitingWidget .badge{
            font-size:10px !important;
            padding:4px 7px !important;
          }

          .pos-page #waitingWidget .btn{
            font-size:10px !important;
            height:26px !important;
            line-height:16px !important;
            padding:4px 8px !important;
          }

          .pos-page .layout{
            display:flex;
            flex-wrap:nowrap;
            gap:8px;
            width:100%;
            height:calc(100vh - 96px);
            min-height:0;
            overflow-x:auto;
            overflow-y:hidden;
            scroll-snap-type:x proximity;
            -webkit-overflow-scrolling:touch;
            padding-bottom:4px;
          }

          .pos-page .layout > div{
            flex:0 0 66.666vw;
            width:66.666vw;
            min-width:66.666vw;
            max-width:66.666vw;
            height:100%;
            min-height:0;
            scroll-snap-align:start;
            overflow:hidden;
          }

          .pos-page .floor{
            width:100%;
            max-width:none;
            height:100%;
            min-height:0;
            overflow:auto;
            align-items:flex-start;
            justify-content:flex-start;
          }

          .pos-page .floor-center{
            min-width:max-content;
            min-height:max-content;
            align-items:flex-start;
            justify-content:flex-start;
          }

          .pos-page .panel{
            position:relative;
            top:auto;
            width:100%;
            height:100%;
            min-height:0;
          }

          .pos-page .panel-body iframe{
            width:100%;
            height:100%;
            min-height:0;
          }

          .pos-page .live-orders-box{
            width:100%;
            height:100%;
            min-height:0;
          }

          .pos-page .live-orders-head{
            padding:8px;
          }

          .pos-page .live-orders-body{
            padding:6px;
          }

          .pos-page .panel-head,
          .pos-page .panel-foot{
            padding:8px 9px;
          }

          .pos-page .panel-body{
            font-size:12px;
          }

          .pos-page .card-body{
            padding:8px !important;
          }

          .pos-page .order-card{
            margin-bottom:8px !important;
          }

          .pos-page .fw-bold{
            line-height:1.25;
          }

          .pos-page .fs-5{
            font-size:14px !important;
          }

          .pos-page .fs-6{
            font-size:12px !important;
          }

          .pos-page .small{
            font-size:10px !important;
          }

          .pos-page .btn{
            font-size:10px;
            line-height:1.15;
            padding:5px 8px;
          }

          .pos-page .btn-sm{
            font-size:10px;
            padding:4px 7px;
          }

          .pos-page .badge{
            font-size:9px;
          }

          .pos-page .sound-pill{
            gap:5px;
            padding:5px 7px;
            font-size:10px;
          }

          .pos-page .dot{
            width:6px;
            height:6px;
          }

          .pos-page .tname{
            font-size:12px;
          }

          .pos-page .tmeta{
            font-size:10px;
          }

          .pos-page .badge-mini,
          .pos-page .addon-badge,
          .pos-page .tbtn{
            font-size:9px;
          }
        }

        @media (max-width: 576px){
          .pos-page{font-size:12px}

          .pos-page .layout{
            height:calc(100vh - 92px);
            gap:6px;
          }

          .pos-page .layout > div{
            flex-basis:66.666vw;
            width:66.666vw;
            min-width:66.666vw;
            max-width:66.666vw;
          }

          .pos-page .pos-title{
            font-size:16px !important;
            min-width:64px;
          }

          .pos-page #waitingWidget{
            gap:6px !important;
            padding:5px 8px !important;
          }

          .pos-page #waitingWidget .vr{
            height:24px;
          }

          .pos-page .live-orders-head{
            padding:7px;
          }

          .pos-page .live-orders-body{
            padding:5px;
          }

          .pos-page .panel-head,
          .pos-page .panel-foot{
            padding:7px 8px;
          }

          .pos-page .card-body{
            padding:7px !important;
          }
        }
      `}</style>


      {optionModal.open && optionModal.product ? (
        <div
          role="presentation"
          onClick={() => setOptionModal({ open: false, product: null, selected: {} })}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.45)',
            zIndex: 5000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '460px',
              maxHeight: '82vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 20px 60px rgba(0,0,0,.22)',
            }}
          >
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <div className="fw-bold fs-5">{optionModal.product.name}</div>
                <div className="small text-muted mt-1">옵션을 선택해주세요.</div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setOptionModal({ open: false, product: null, selected: {} })}
              />
            </div>

            {(optionModal.product.option_groups || []).map((group) => (
              <div key={group.id} className="mb-4">
                <div className="fw-bold mb-2">
                  {group.group_name}
                  {String(group.required || 'N').toUpperCase() === 'Y' ? (
                    <span className="text-danger ms-1">*</span>
                  ) : null}
                </div>

                <div className="d-grid gap-2">
                  {(group.items || []).map((item) => {
                    const active = Number(optionModal.selected[String(group.id)] || 0) === Number(item.id);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`btn text-start ${active ? 'btn-dark' : 'btn-outline-secondary'}`}
                        onClick={() => setOptionModal((prev) => ({
                          ...prev,
                          selected: {
                            ...prev.selected,
                            [String(group.id)]: Number(item.id),
                          },
                        }))}
                      >
                        <span>{item.item_name}</span>
                        {Number(item.extra_price || 0) > 0 ? (
                          <span className="float-end">
                            +{Number(item.extra_price || 0).toLocaleString()}원
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            <div className="border-top pt-3">
              <div className="d-flex justify-content-between mb-3">
                <span className="fw-bold">예상 단가</span>
                <span className="fw-bold">
                  {(
                    Number(optionModal.product.display_price ?? optionModal.product.price ?? 0) +
                    (optionModal.product.option_groups || []).reduce((sum, group) => {
                      const itemId = Number(optionModal.selected[String(group.id)] || 0);
                      const item = (group.items || []).find((row) => Number(row.id) === itemId);
                      return sum + Number(item?.extra_price || 0);
                    }, 0)
                  ).toLocaleString()}원
                </span>
              </div>

              <button type="button" className="btn btn-dark w-100" onClick={confirmOptionProduct}>
                장바구니 담기
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="pos-page">
        <div className="container-fluid py-3 px-2">
          <div className="pos-topbar mb-2">
            <h5 className="pos-title m-0">{boot.shop_name} POS</h5>

            <div className="waiting-wrap d-flex gap-2 flex-shrink-0">
              <div
                id="waitingWidget"
                className="d-flex align-items-center gap-3 px-3 py-1 border rounded-pill bg-white shadow-sm"
              >
                <div className="d-flex align-items-center gap-2">
                  <b className="small text-primary">웨이팅</b>
                  <span className="badge rounded-pill text-bg-secondary">
                    대기 <span>{waitingSummary.waiting_cnt}</span>
                  </span>
                  <span className="badge rounded-pill text-bg-warning">
                    호출 <span>{waitingSummary.called_cnt}</span>
                  </span>
                </div>

                <div className="vr"></div>
                <div className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                  {waitingSummary.next_text}
                </div>

                <a
                  className="btn btn-xs btn-outline-primary py-0 px-2"
                  href="/seller/waiting/manage"
                  style={{ fontSize: '11px', height: '20px', lineHeight: '18px' }}
                >
                  관리
                </a>
              </div>
            </div>
          </div>

          {kioskToast.visible ? (
            <div className="alert alert-warning py-2 mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <b>키오스크 카운터 결제요청</b>
                  <span className="ms-2">
                    코드: <span className="fw-bold">{kioskToast.code}</span>
                  </span>
                  <span className="ms-2">
                    금액: <span className="fw-bold">{kioskToast.amount}</span>
                  </span>
                  <div
                    className="mt-2 small"
                    style={{ display: kioskToast.itemsHtml ? '' : 'none' }}
                    dangerouslySetInnerHTML={{ __html: kioskToast.itemsHtml }}
                  />
                </div>

                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-success" type="button" onClick={handleKioskCounterPaid}>
                    결제완료
                  </button>
                  <button
                    className="btn btn-sm btn-outline-dark"
                    type="button"
                    onClick={() => setKioskToast({
                      visible: false,
                      code: '-',
                      amount: '-',
                      itemsHtml: '',
                      token: '',
                    })}
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <div className="layout">
            <div>
              <div className="live-orders-box">
                <div className="live-orders-head">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="fw-bold">실시간 주문관리</div>

                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className={
                          pushStatus.subscribed
                            ? 'btn btn-success btn-sm'
                            : 'btn btn-outline-primary btn-sm'
                        }
                        disabled={
                          pushStatus.loading ||
                          !pushStatus.supported ||
                          pushStatus.permission === 'denied'
                        }
                        onClick={enablePushNotifications}
                        title={pushStatus.message}
                      >
                        {pushStatus.loading
                          ? '알림 확인 중'
                          : pushStatus.subscribed
                            ? '알림 ON'
                            : '알림 켜기'}
                      </button>

                      <span className="sound-pill" onClick={enableSound}>
                        <span className={`dot ${soundEnabled ? 'on' : ''}`}></span>
                        <span>{soundEnabled ? '소리 ON' : '소리 OFF'}</span>
                      </span>
                    </div>
                  </div>

                  <div className="d-flex gap-2 flex-wrap mb-2">
                    {[
                      ['ALL', '전체'],
                      ['NEW', '새주문'],
                      ['COOKING', '준비중'],
                      ['DONE', '완료'],
                      ['CANCELLED', '취소'],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        className={`btn btn-outline-secondary btn-sm live-status-btn ${liveStatus === key ? 'active' : ''}`}
                        onClick={() => {
                          setLiveStatus(key);
                          setLivePage(1);
                          setLiveFirstLoaded(false);
                          setLiveLastLatestId(0);
                          fetchLiveOrders(false, key, 1);
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="live-orders-body">
                  <div id="liveOrdersWrapReact">
                    {renderLiveOrders()}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="floor p-2" ref={floorWrapRef}>
                <div className="floor-center">
                  <div
                    className="floor-inner"
                    style={{
                      width: `${boot.canvas_w}px`,
                      height: `${boot.canvas_h}px`,
                      transform: `scale(${scale})`,
                      backgroundImage: boot.bg ? `url('${boot.bg}')` : undefined,
                    }}
                  >
                    {boot.tables.map((t) => {
                      const st = stateMap[String(Number(t.id))] || {};
                      const isOrdered = String(st.status || '') === 'ORDERED';
                      const hasAddonBadge = !!st.has_addon;
                      const money = Number(st.money || 0);
                      const selected = Number(selectedTableId) === Number(t.id);

                      return (
                        <div
                          key={t.id}
                          className={`tbox ${isOrdered ? 'st-new' : 'st-idle'} ${hasAddonBadge ? 'box-blink' : ''}`}
                          style={{
                            left: `${parseInt(t.x || 0, 10)}px`,
                            top: `${parseInt(t.y || 0, 10)}px`,
                            width: `${parseInt(t.w || 0, 10)}px`,
                            height: `${parseInt(t.h || 0, 10)}px`,
                            outline: selected ? '3px solid #0d6efd' : 'none',
                          }}
                          onClick={() => showOrderDetail(parseInt(t.id, 10))}
                        >
                          <span
                            className="badge text-bg-danger addon-badge blink"
                            style={{ display: hasAddonBadge ? '' : 'none' }}
                          >
                            추가
                          </span>

                          <div className="tname">{t.name}</div>
                          <div className="tmeta">
                            <span className={`badge ${isOrdered ? 'text-bg-danger' : 'text-bg-secondary'} badge-mini`}>
                              {isOrdered ? '주문' : '대기'}
                            </span>
                          </div>
                          <div className="tmeta" style={{ display: money > 0 ? '' : 'none' }}>
                            {money > 0 ? `₩ ${money.toLocaleString()}` : ''}
                          </div>

                          <div
                            className="tbtn"
                            onClick={(e) => {
                              e.stopPropagation();
                              showMenu(parseInt(t.id, 10));
                            }}
                          >
                            수동추가
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-3 small text-muted"></div>
            </div>

            <div className="panel">
              <div className="panel-head">
                <div className="fw-bold">{panelTitle}</div>

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={showHistory}
                    style={{ display: currentOrderId > 0 && panelMode !== 'live-order-detail' ? '' : 'none' }}
                  >
                    주문이력
                  </button>

                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleAck}
                    style={{ display: hasAddon ? '' : 'none' }}
                  >
                    주문확인
                  </button>

                  <button
                    className="btn btn-success btn-sm"
                    onClick={handlePaid}
                    disabled={!selectedTableId}
                    style={{ display: panelMode === 'live-order-detail' ? 'none' : '' }}
                  >
                    결제완료
                  </button>
                </div>
              </div>

              <div className="panel-body">
                {panelMode === 'menu' ? (
                  renderMenuPanel()
                ) : panelMode === 'cart' ? (
                  renderCartPanel()
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: panelHtml }} />
                )}
              </div>

              <div className="panel-foot small text-muted">
                <span>{panelHint}</span>
                {currentOrderDate ? <span className="ms-2">({currentOrderDate})</span> : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}